/**
 * Enrolling a second factor, in two stages.
 *
 * **The first version of this screen enabled MFA the moment you pressed the button.** The
 * secret was generated, written to your account and switched on, and only *then* were you
 * shown the QR code and the recovery codes. From that instant the account demanded a
 * factor you had not configured yet. Close the tab, lose the network, misread the codes —
 * and you were locked out, permanently, because this product has no MFA-disable endpoint
 * and no password reset. The confirmation checkbox did not help: it gated leaving the
 * page, and the damage was done before the page rendered.
 *
 * So enrolment is now *offered* and then *confirmed*:
 *
 * 1. **Offer.** The server generates a secret and ten recovery codes and hands them back
 *    with an `enrolment_token` that carries the sealed secret. **Nothing is stored.** The
 *    account is untouched and still signs in with a password alone.
 * 2. **Confirm.** You scan, you type a code your authenticator produced, and you state
 *    that the recovery codes are saved. Only when that code verifies does the server
 *    store the secret and switch MFA on.
 *
 * Abandoning stage 1 — cancel, close the tab, reload — needs no cleanup and leaves no
 * half-enrolled account, because the pending secret only ever existed inside a token this
 * component holds in memory. A reload drops it, and a dropped token is a discarded
 * enrolment.
 *
 * Two properties this screen still has to get right:
 *
 * - **The codes are displayed once.** Starting over issues a *new* secret and new codes,
 *   so a "show them again" button would be a button that silently breaks an authenticator
 *   somebody has already configured.
 * - **Nothing is persisted in the browser.** No local storage, no query cache — the token
 *   and the codes live in component state for as long as the tab is open, and then they
 *   are gone. That is a deliberate part of stage 1 being abandonable.
 */

import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { ChangePasswordRequest, MfaEnrolment, MfaEnrolmentConfirmRequest } from '@/lib/schemas';
import { Badge, Button, Card, CopyButton, ErrorState, QrCode } from '@/components/ui';

/** The MFA enrolment screen. */
export function MfaPage(): ReactElement {
  const [pending, setPending] = useState<MfaEnrolment | null>(null);
  const [enabled, setEnabled] = useState(false);

  const begin = useMutation({
    mutationFn: () => request<MfaEnrolment>('/auth/mfa/enrolment', { method: 'POST' }),
    onSuccess: setPending,
  });

  if (pending) {
    return (
      <PendingEnrolment
        enrolment={pending}
        onCancel={() => setPending(null)}
        onConfirmed={() => {
          setPending(null);
          setEnabled(true);
        }}
      />
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      <h1 className="text-lg font-semibold">Two-factor authentication</h1>

      {enabled && (
        <Card>
          <div className="space-y-2 p-4">
            <Badge tone="ok">Enabled</Badge>
            <p className="text-sm">
              Your next sign-in will ask for a code from your authenticator.
            </p>
          </div>
        </Card>
      )}

      <p className="text-sm text-muted">
        A time-based one-time password (TOTP), from any standard authenticator. You will be
        shown a QR code and ten single-use recovery codes, and then asked for a code from
        your authenticator to prove it is working.{' '}
        <strong>Two-factor authentication is not switched on until that code verifies</strong>
        , so you can stop at any point before then and nothing will have changed.
      </p>

      <ChangePassword />

      <Card>
        <div className="space-y-2 p-4">
          <p className="text-sm text-muted">
            If you already have MFA on this account, completing a new enrolment issues a new
            secret and new codes and <strong>invalidates the ones you have</strong>. Only do
            it if you have lost access to your authenticator. The administration screen shows
            which accounts have MFA enabled.
          </p>
        </div>
      </Card>

      {begin.error != null && <ErrorState error={begin.error} />}

      <Button onClick={() => begin.mutate()} disabled={begin.isPending} tone="primary">
        {begin.isPending ? 'Preparing…' : 'Enable two-factor authentication'}
      </Button>
    </div>
  );
}

/**
 * Stage 1: the offer. Displayed, not yet in force.
 *
 * @param props - The offered enrolment and the two ways out of it.
 * @returns The enrolment screen.
 */
function PendingEnrolment({
  enrolment,
  onCancel,
  onConfirmed,
}: {
  enrolment: MfaEnrolment;
  onCancel: () => void;
  onConfirmed: () => void;
}): ReactElement {
  const [code, setCode] = useState('');
  const [saved, setSaved] = useState(false);
  const codes = enrolment.recovery_codes.join('\n');

  const confirm = useMutation({
    mutationFn: () =>
      request<null, MfaEnrolmentConfirmRequest>('/auth/mfa/enrolment:confirm', {
        method: 'POST',
        body: { enrolment_token: enrolment.enrolment_token, code },
      }),
    onSuccess: onConfirmed,
  });

  // Both conditions, not either: the code proves the authenticator works, and the
  // checkbox is the only claim anyone will ever have that the codes were written down.
  const ready = saved && /^\d{6}$/.test(code) && !confirm.isPending;

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (ready) confirm.mutate();
  }

  return (
    <div className="max-w-2xl space-y-4">
      <h1 className="text-lg font-semibold">Set up two-factor authentication</h1>

      <Card>
        <div className="space-y-1 p-4">
          <Badge tone="warn">Not enabled yet</Badge>
          <p data-testid="mfa-not-yet-enabled" className="text-sm text-muted">
            Nothing has changed on your account. If you leave, reload, or cancel now, this
            secret is discarded and you carry on signing in with a password alone.
          </p>
        </div>
      </Card>

      <Card title="1. Add the account to your authenticator">
        <div className="space-y-3 p-4">
          <div className="flex flex-wrap items-start gap-4">
            <QrCode
              value={enrolment.provisioning_uri}
              label="Scan this with your authenticator app"
            />
            <div className="flex-1 space-y-3 text-xs">
              <p className="text-muted">
                Scan the code. If your authenticator cannot scan — a desktop app, or a phone
                that cannot see this screen — enter the secret by hand instead.
              </p>

              <div className="space-y-1">
                <div className="uppercase tracking-wide text-muted">Secret</div>
                <div className="flex flex-wrap items-center gap-2">
                  <code
                    data-testid="mfa-secret"
                    className="select-all break-all rounded bg-base px-2 py-1 font-mono"
                  >
                    {enrolment.secret}
                  </code>
                  <CopyButton value={enrolment.secret} label="secret" />
                </div>
              </div>

              <div className="space-y-1">
                <div className="uppercase tracking-wide text-muted">Provisioning URI</div>
                <div className="flex flex-wrap items-center gap-2">
                  <code
                    data-testid="mfa-provisioning-uri"
                    className="select-all break-all rounded bg-base px-2 py-1 font-mono"
                  >
                    {enrolment.provisioning_uri}
                  </code>
                  <CopyButton value={enrolment.provisioning_uri} label="provisioning URI" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card title="2. Store your recovery codes">
        <div className="space-y-3 p-4">
          <p className="text-xs text-muted">
            Ten single-use codes. Each works once, in place of your authenticator. Keep them
            somewhere you can reach <em>without</em> this account. They are shown only now —
            only their hashes are stored, and only once you confirm below.
          </p>

          {/* A single selectable block as well as the list: copying ten separate
              elements by hand is how one of them gets missed. */}
          <textarea
            data-testid="mfa-recovery-codes-block"
            readOnly
            rows={5}
            value={codes}
            aria-label="Recovery codes"
            className="w-full select-all rounded border border-line bg-base p-2 font-mono text-xs"
          />
          <CopyButton value={codes} label="recovery codes" />

          <ul data-testid="mfa-recovery-codes" className="grid grid-cols-2 gap-1">
            {enrolment.recovery_codes.map((recoveryCode) => (
              <li key={recoveryCode} className="rounded bg-base px-2 py-1 font-mono text-xs">
                {recoveryCode}
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <Card title="3. Confirm">
        <form onSubmit={onSubmit} className="space-y-3 p-4">
          <p className="text-xs text-muted">
            Enter the six-digit code your authenticator is showing now. This is what proves
            the secret arrived intact — and it is the step that actually enables two-factor
            authentication.
          </p>

          <label className="block text-sm">
            <span className="text-muted">Code from your authenticator</span>
            <input
              data-testid="mfa-confirm-code"
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              required
              value={code}
              onChange={(event) => setCode(event.target.value.trim())}
              className="mt-1 w-32 rounded border border-line bg-surface px-2 py-1.5 font-mono text-sm"
            />
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={saved}
              onChange={(event) => setSaved(event.target.checked)}
              className="mt-1"
            />
            I have safely stored my recovery codes.
          </label>

          {confirm.error != null && <ErrorState error={confirm.error} />}

          <div className="flex items-center gap-2">
            <Button type="submit" tone="primary" disabled={!ready}>
              {confirm.isPending ? 'Verifying…' : 'Confirm & Enable MFA'}
            </Button>
            <Button type="button" onClick={onCancel} disabled={confirm.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

/**
 * Changing your own password.
 *
 * **There was no way to do this.** A credential believed compromised could only be retired
 * by building a new tenant, which is not a remedy. Deliberately the narrow version: your
 * own password, proving you know the current one. No reset flow — that needs a delivery
 * channel this platform does not have — and no administrator override, because an
 * administrator who can set your password can be you.
 *
 * @returns The form.
 */
function ChangePassword(): ReactElement {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () =>
      request<null, ChangePasswordRequest>('/auth/password', {
        method: 'POST',
        body: { current_password: current, new_password: next },
      }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setDone(true);
    },
  });

  return (
    <Card title="Change your password">
      <form
        className="space-y-3 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          setDone(false);
          change.mutate();
        }}
      >
        <p className="text-xs text-muted">
          Every other session signs out. A password change that left old sessions alive
          would be a change in name only.
        </p>

        <label className="block text-sm">
          <span className="text-muted">Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            data-testid="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            className="mt-1 w-full max-w-sm rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </label>

        <label className="block text-sm">
          <span className="text-muted">New password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            data-testid="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            className="mt-1 w-full max-w-sm rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </label>

        {change.error != null && <ErrorState error={change.error} />}
        {done && (
          <p role="status" data-testid="password-changed" className="text-sm text-ok">
            Password changed. Other sessions have been signed out.
          </p>
        )}

        <Button type="submit" disabled={change.isPending || !current || next.length < 12}>
          {change.isPending ? 'Changing…' : 'Change password'}
        </Button>
      </form>
    </Card>
  );
}
